import { useEffect, useState } from "react";
import { X, UserCircle2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · Kunden-Picker als Popup-Modal.
 *
 * Wird geöffnet, sobald ein Admin oder Concierge im Header-View-Switcher
 * auf „Kunden-Ansicht" klickt. Der Nutzer wählt einen Kunden aus der
 * Liste; per Klick öffnet sich die Kunden-App als IMPERSONATION-Session
 * in einem NEUEN TAB (2 Minuten gültig, im Kundenansicht-Audit protokolliert).
 *
 * Dass der neue Tab die Ansicht trägt und der aktuelle Tab die Admin-
 * Session behält, ist essentiell: sonst würde der Admin sein
 * Access-Token gegen ein Kunden-Token tauschen und müsste sich neu
 * einloggen, um zurück zu Admin zu kommen.
 */
export default function CustomerPickerModal({ open, onClose }) {
  const [customers, setCustomers] = useState(null);
  const [pickingId, setPickingId] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/team/customers");
        setCustomers(
          (data || [])
            .filter((u) => u.role === "customer" || u.role === "family")
            .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "")),
        );
      } catch { setCustomers([]); }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const pick = async (c) => {
    setPickingId(c.id);
    try {
      const { data } = await api.post(`/team/customers/${c.id}/impersonation-link`);
      const url = data?.url;
      if (!url) throw new Error("Impersonation-Link fehlt in Response.");
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Popup wurde blockiert. Bitte Popups für diese Seite erlauben.");
      } else {
        toast.success(`Kundenansicht als ${c.name || c.email} in neuem Tab geöffnet.`);
        onClose();
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Kundenansicht konnte nicht geöffnet werden.");
    } finally {
      setPickingId(null);
    }
  };

  const filtered = (() => {
    if (!customers) return null;
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s),
    );
  })();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="customer-picker-modal">
      <div
        className="absolute inset-0"
        style={{ background: withAlpha(BRAND_COLORS.navyDark, 0.55) }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-picker-title"
        className="relative bg-ivory w-full max-w-xl max-h-[85vh] flex flex-col shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-navy/10">
          <div>
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Kunden-Ansicht</div>
            <div id="customer-picker-title" className="font-display text-xl text-navy mt-1">
              Kunde auswählen
            </div>
          </div>
          <button
            type="button"
            data-testid="customer-picker-close"
            onClick={onClose}
            aria-label="Schließen"
            className="text-navy-dark/60 hover:text-navy p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-navy/10">
          <p className="text-[12.5px] text-navy-dark/65 leading-relaxed mb-3">
            Wähle einen Kunden, dessen Ansicht du öffnen willst. Der Kunden-View
            läuft <strong>2 Minuten</strong> in einem NEUEN TAB — dein Admin-Fenster
            bleibt aktiv. Jeder Aufruf wird im Kundenansicht-Audit protokolliert.
          </p>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Kunde suchen · Name oder E-Mail"
            data-testid="customer-picker-search"
            autoFocus
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filtered === null ? (
            <div className="flex items-center gap-2 text-[13px] text-navy-dark/50 italic p-4">
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.6} /> Lade Kunden …
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="p-6 text-center text-[13px] text-navy-dark/60 border border-dashed border-navy/20"
              data-testid="customer-picker-empty"
            >
              {q ? `Keine Kunden gefunden für „${q}".` : "Keine Kunden gefunden."}
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c)}
                disabled={pickingId !== null}
                data-testid={`customer-picker-row-${c.id}`}
                className="w-full bg-white border border-navy/15 hover:border-gold p-4 flex items-center justify-between gap-4 text-left transition-colors disabled:opacity-60 group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: withAlpha(BRAND_COLORS.navy, 0.08), color: BRAND_COLORS.navy }}
                  >
                    <UserCircle2 className="w-5 h-5" strokeWidth={1.4} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-[15px] text-navy leading-tight truncate">
                      {c.name || c.email}
                    </div>
                    <div className="text-[11.5px] text-navy-dark/60 truncate">
                      {c.email}
                      {c.role === "family" && (
                        <span className="ml-2 label-caps" style={{ color: BRAND_COLORS.goldDark }}>· Familie</span>
                      )}
                    </div>
                  </div>
                </div>
                {pickingId === c.id ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gold" strokeWidth={1.6} />
                ) : (
                  <ExternalLink className="w-4 h-4 text-navy-dark/40 group-hover:text-gold" strokeWidth={1.6} />
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
